import { reaction, makeAutoObservable } from 'mobx';
import dayjs from '../Utils/dayjs';
import { WorkoutRunner } from './WorkoutRunner';
import { AnalyticsStore } from './Firebase/AnalyticsStore';

export type DateRangeKey = 'week' | 'month' | 'year';

function createWorkoutAnalyticsStore(workoutRunner: WorkoutRunner, analytics: AnalyticsStore) {
  const store = makeAutoObservable({
    workoutRunner,
    analytics,
    chosenWeekDiff: 0,
    chosenYearPickerYear: dayjs().year(),
    chosenMonthPickerMonth: dayjs().month(),
    chosenMonthPickerYear: dayjs().year(),
    periodFilter: 'month' as DateRangeKey,

    setChosenWeekDiff(diff: number) {
      if (diff > 0) {
        diff = 0;
      }
      this.chosenWeekDiff = diff;
    },

    setPeriodFilter(periodFilter: DateRangeKey) {
      this.periodFilter = periodFilter;
    },

    get selectedDateRange(): [Date, Date] {
      switch (this.periodFilter) {
        case 'week': {
          const choice = dayjs().add(this.chosenWeekDiff, 'week');
          return [choice.startOf('week').toDate(), choice.endOf('week').toDate()];
        }
        case 'year': {
          const choice = dayjs(`${this.chosenYearPickerYear}`, 'YYYY');
          return [choice.startOf('year').toDate(), choice.endOf('year').toDate()];
        }
        case 'month':
        default: {
          const choice = dayjs(this.chosenMonthDisplayString, 'MMMM YYYY');
          return [choice.startOf('month').toDate(), choice.endOf('month').toDate()];
        }
      }
    },

    setChosenMonthPickerMonth(month: number) {
      const chosenMonthYear = `${dayjs().month(month).format('MMMM')} ${this.chosenMonthPickerYear}`;
      const today = dayjs();
      const isBefore = dayjs(chosenMonthYear, 'MMMM YYYY').isBefore(today);
      if (isBefore) {
        this.chosenMonthPickerMonth = month;
      }
    },

    setChosenMonthPickerYear(year: number) {
      const chosenMonthYear = `${dayjs().month(this.chosenMonthPickerMonth).format('MMMM')} ${year}`;
      const today = dayjs();
      const isBefore = dayjs(chosenMonthYear, 'MMMM YYYY').isBefore(today);
      if (isBefore) {
        this.chosenMonthPickerYear = year;
      }
    },

    setChosenYearPickerYear(year: number) {
      const today = dayjs();
      const isBefore = dayjs(`${year}`, 'YYYY').isBefore(today);
      if (isBefore) {
        this.chosenYearPickerYear = year;
      }
    },

    setCurrentPeriodInPicker() {
      const today = dayjs();
      if (this.periodFilter === 'week') {
        this.setChosenWeekDiff(0);
      }

      if (this.periodFilter === 'month') {
        this.setChosenMonthPickerMonth(today.month());
        this.setChosenMonthPickerYear(today.year());
      }

      if (this.periodFilter === 'year') {
        this.setChosenYearPickerYear(today.year());
      }
    },

    get chosenMonthDisplayString() {
      return `${dayjs().month(this.chosenMonthPickerMonth).format('MMMM')} ${this.chosenMonthPickerYear}`;
    },
  });

  reaction(
    () => workoutRunner.workoutLog.lastSet?.startDateTime,
    (startDateTime) => {
      const { workoutLog: { lastSet } } = workoutRunner;
      if (startDateTime && lastSet) {
        analytics.logEvent('Exercise_Started', {
          startDateTime: startDateTime.toISOString(),
          setId: lastSet.setId,
          variationId: lastSet.exercise.id,
          name: lastSet.exercise.name,
        });
      }
    },
    { name: 'Log Exercise Start' },
  );

  reaction(
    () => workoutRunner.workoutLog.lastSet?.endDateTime,
    (endDateTime) => {
      const { workoutLog: { lastSet } } = workoutRunner;
      if (endDateTime && lastSet) {
        analytics.logEvent('Exercise_Stopped', {
          endDateTime: endDateTime.toISOString(),
          setId: lastSet.setId,
          variationId: lastSet.exerciseBlockLog.exercise.id,
          name: lastSet.exercise.name,
        });
      }
    },
    { name: 'Log Exercise Stop' },
  );

  return store;
}

export type WorkoutAnalyticsStore = ReturnType<typeof createWorkoutAnalyticsStore>;
export const createWorkoutAnalyticsStoreInstance = createWorkoutAnalyticsStore;
